import { ButtonLink } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { createSignal } from '@react-rxjs/utils'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { catchError, EMPTY, filter, from, map, switchMap, tap, timer } from 'rxjs'
import styled from 'styled-components'

import { checkAndDiscardQueryResponse } from '../../../../utils/graphqlTools'
import Link from '../../../global/Link'
import ConfirmationModal from './ConfirmationModal'

const StyledAddressElement = styled.address`
  font-style: inherit;
  min-width: 100%;
`

const StyledActions = styled.div`
  // push actions to bottom of component, aligning when displayed in grid
  margin-top: auto;
`

const [deleteAddressInput$, setDeleteAddressInput] = createSignal()

const Address = ({ address, className, showDelete, onDeleteCustomerAddress }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const onConfirm = () => setDeleteAddressInput(address.id)

  useEffect(() => {
    const addressId = address?.id
    if (!addressId) {
      return
    }
    const subscription = deleteAddressInput$
      .pipe(
        // ignore other addresses
        filter((id) => id === addressId),
        tap(() => {
          setIsDeleting(true)
          setIsModalOpen(false)
        }),
        switchMap((id) =>
          from(onDeleteCustomerAddress(id)).pipe(
            map(checkAndDiscardQueryResponse),
            // if successful, wait a moment before setting isDeleting to false
            switchMap(() => timer(2000)),
            // in most cases, this component will unmount before the timer completes
            tap(() => setIsDeleting(false)),
            catchError(() => {
              setIsDeleting(false)
              return EMPTY
            }),
          ),
        ),
      )
      .subscribe()
    return () => subscription.unsubscribe()
  }, [address, onDeleteCustomerAddress])

  if (address) {
    const region = address.region.region_code || address.region.region
    return (
      <div className={`${className} address`}>
        <StyledAddressElement>
          <div>
            <strong>{`${address.firstname} ${address.lastname}`.trim()}</strong>
          </div>
          <div>{address.street.join(' ').trim()}</div>
          <div>
            {address.city}, {region?.trim() ? `${region}, ` : ''}
            {address.country_code} {address.postcode}
          </div>
          {address.telephone && <div>{address.telephone}</div>}
        </StyledAddressElement>
        <StyledActions>
          <Link to={`/account/addresses/${address.id}`}>Edit</Link>
          {!showDelete ? null : isDeleting ? (
            <CircularProgress size={14} />
          ) : (
            <ButtonLink onClick={() => setIsModalOpen(true)} text="Delete" underline />
          )}
        </StyledActions>
        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={onConfirm}
          address={address}
          toggleModal={() => setIsModalOpen(!isModalOpen)}
        />
      </div>
    )
  }

  return null
}

Address.defaultProps = {
  className: '',
  showDelete: true,
  onDeleteCustomerAddress: null,
}

Address.propTypes = {
  address: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    city: PropTypes.string,
    id: PropTypes.number,
    region: PropTypes.shape({
      region_code: PropTypes.string,
      region: PropTypes.string,
    }),
    country_code: PropTypes.string,
    postcode: PropTypes.string,
    street: PropTypes.arrayOf(PropTypes.string),
    telephone: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  onDeleteCustomerAddress: PropTypes.func,
  showDelete: PropTypes.bool,
}

const StyledAddress = styled(Address)(({ theme }) => ({
  padding: '22px 25px',
  display: 'flex',
  flexWrap: 'wrap',
  'a, a:visited': {
    color: theme.colors.grayscale.black,
  },
  'a:first-of-type': {
    marginRight: '22px',
  },
  address: {
    lineHeight: '18px',
    marginBottom: '12px',
    div: {
      marginBottom: '3px',
    },
  },
}))

export default StyledAddress
